import { Controller } from "@hotwired/stimulus"

// Stage links on the "Add item" form BEFORE the item exists. Picks are
// held as full-width rows carrying hidden `pending_links[N][...]`
// fields; meeting_items#create turns them into MeetingItemLinks once
// the item saves. Mirrors meeting_link_controller's picker, but it
// never POSTs — it just accumulates form fields.
export default class extends Controller {
  static targets = [
    "trigger", "picker", "typeSelect", "kindSelect", "search", "results", "list"
  ]
  static values = { candidatesUrl: String }

  connect() {
    this._searchTimer = null
    this._index = 0
  }

  openPicker() {
    if (!this.hasPickerTarget) return
    this.pickerTarget.classList.remove("hidden")
    if (this.hasSearchTarget) {
      this.searchTarget.value = ""
      this.searchTarget.focus()
    }
    this._loadCandidates()
  }

  closePicker() {
    if (this.hasPickerTarget) this.pickerTarget.classList.add("hidden")
  }

  typeChanged() {
    this._loadCandidates()
  }

  searchChanged() {
    if (this._searchTimer) clearTimeout(this._searchTimer)
    this._searchTimer = setTimeout(() => this._loadCandidates(), 220)
  }

  async _loadCandidates() {
    if (!this.hasResultsTarget) return
    const type = this.typeSelectTarget.value
    const q = this.hasSearchTarget ? this.searchTarget.value : ""

    this.resultsTarget.innerHTML = `<li class="px-2 py-2 text-[12px] text-gray-400">Searching…</li>`

    try {
      const url = `${this.candidatesUrlValue}?linkable_type=${encodeURIComponent(type)}&q=${encodeURIComponent(q)}`
      const resp = await fetch(url, {
        headers: { "Accept": "application/json", "X-CSRF-Token": this._csrf() },
        credentials: "same-origin"
      })
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
      const data = await resp.json()
      this._renderCandidates(data.rows || [], type)
    } catch (e) {
      console.error("[MeetingItemLinkDraft] picker error", e)
      this.resultsTarget.innerHTML = `<li class="px-2 py-2 text-[12px] text-rose-700">Failed to load — try again.</li>`
    }
  }

  _renderCandidates(rows, type) {
    if (rows.length === 0) {
      this.resultsTarget.innerHTML = `<li class="px-2 py-2 text-[12px] text-gray-400">No matching ${this._esc(type)} found.</li>`
      return
    }
    this.resultsTarget.innerHTML = rows.map(row => {
      const thumb = row.thumb
        ? `<img src="${this._esc(row.thumb)}" alt="" loading="lazy" class="w-8 h-8 rounded object-cover bg-gray-100 shrink-0">`
        : ""
      return `
      <li class="px-2 py-1.5 hover:bg-brand/5 flex items-center gap-2 cursor-pointer"
          data-action="click->meeting-item-link-draft#pickCandidate"
          data-candidate-id="${row.id}"
          data-candidate-label="${this._esc(row.label)}">
        ${thumb}
        <span class="text-[12px] text-gray-700 flex-1 truncate">${this._esc(row.label)}</span>
        <span class="text-[10px] text-brand font-medium">+ Add</span>
      </li>`
    }).join("")
  }

  pickCandidate(event) {
    const id = event.currentTarget.dataset.candidateId
    const label = event.currentTarget.dataset.candidateLabel || ""
    const type = this.typeSelectTarget.value
    // The link-kind picker was removed; default to "references". Guard
    // in case the select is ever reintroduced.
    const kind = this.hasKindSelectTarget ? this.kindSelectTarget.value : "references"
    if (!id) return

    // De-dupe: same type + record can't be staged twice.
    if (this.listTarget.querySelector(`[data-link-key="${type}:${id}"]`)) {
      this.closePicker()
      return
    }
    this._appendRow({ id, label, type, kind })
    this.closePicker()
  }

  removeRow(event) {
    const row = event.currentTarget.closest("[data-pending-row]")
    if (row) row.remove()
  }

  _appendRow({ id, label, type, kind }) {
    if (!this.hasListTarget) return
    const i = this._index++
    const html = `
      <div class="flex items-center gap-2 px-2 py-1.5 rounded-lg border border-gray-200 bg-white"
           data-pending-row data-link-key="${this._esc(type)}:${this._esc(id)}">
        <span class="shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full bg-brand/10 text-brand">
          <svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M13.828 10.172a4 4 0 015.656 0l1.414 1.414a4 4 0 010 5.656l-2.828 2.828a4 4 0 01-5.656 0M10.172 13.828a4 4 0 01-5.656 0L3.1 12.414a4 4 0 010-5.656L5.929 3.93a4 4 0 015.656 0"/></svg>
        </span>
        <div class="flex-1 min-w-0">
          <div class="text-[12px] text-gray-800 truncate">${this._esc(label)}</div>
          <div class="text-[10px] text-gray-500">${this._esc(type)} · ${this._esc(this._humanize(kind))}</div>
        </div>
        <button type="button" data-action="click->meeting-item-link-draft#removeRow"
                class="shrink-0 text-gray-400 hover:text-gray-600 cursor-pointer leading-none" title="Remove">×</button>
        <input type="hidden" name="pending_links[${i}][linkable_type]" value="${this._esc(type)}">
        <input type="hidden" name="pending_links[${i}][linkable_id]" value="${this._esc(id)}">
        <input type="hidden" name="pending_links[${i}][link_kind]" value="${this._esc(kind)}">
      </div>`
    this.listTarget.insertAdjacentHTML("beforeend", html)
  }

  _humanize(str) {
    return String(str || "").replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())
  }

  _csrf() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }

  _esc(str) {
    if (str == null) return ""
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;")
  }
}
